import * as THREE from 'three';
import type { ContentRegistry } from '../content/registry';
import { AFFIX_BITS } from '../content/affixes';
import type { BossDef, BulletVisual, PrimitiveGeo, VisualDef } from '../content/types';
import type { World } from '../sim/world';
import { MAX_ENEMIES, MAX_HAZARDS, MAX_PICKUPS, MAX_PROJECTILES, wrapDelta } from '../sim/world';
import { MAX_PETS } from '../sim/world';
import { PETS } from '../content/pets';
import { primitiveGeometry, toonMaterial } from './geometry';
import { instancedOutline } from './outline';
import type { ModelLibrary } from './models';
import type { ColorblindMode } from '../meta/save';
import { PLAYER_PROJECTILE_LOOKS, projectileLookIndex } from './combatVisuals';
import { ENEMY_PROJECTILE_LOOKS, enemyProjectileLookIndex } from './enemyProjectileVisuals';

const GEOS: PrimitiveGeo[] = ['sphere', 'box', 'cone', 'capsule', 'octa', 'spike', 'disc', 'torus', 'blob'];

/** One InstancedMesh (+ outline twin) worth of enemy visuals — either a
 * primitive shape shared by every enemy using it, or one merged GLTF model
 * shared by every enemy referencing that path. See render/models.ts. */
interface EnemyBucket {
  mesh: THREE.InstancedMesh;
  outline: THREE.InstancedMesh;
  /** True for a GLTF-backed bucket: its material uses baked vertex colors, so
   * the per-instance tint should start from white instead of `visual.color`. */
  isModel: boolean;
}

const _m = new THREE.Matrix4();
const _p = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3();
const _c = new THREE.Color();
const _up = new THREE.Vector3(0, 1, 0);
const _qs = new THREE.Quaternion();
const _fwd = new THREE.Vector3(0, 0, 1);

const _elColor = new THREE.Color();
const WHITE = new THREE.Color(0xffffff);

/** Rounded, beveled heart silhouette in the XY plane, facing local +Z. */
function heartGeometry(): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(0, -0.4);
  shape.bezierCurveTo(-0.13, -0.25, -0.43, -0.04, -0.43, 0.17);
  shape.bezierCurveTo(-0.43, 0.43, -0.12, 0.49, 0, 0.25);
  shape.bezierCurveTo(0.12, 0.49, 0.43, 0.43, 0.43, 0.17);
  shape.bezierCurveTo(0.43, -0.04, 0.13, -0.25, 0, -0.4);
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: 0.12, steps: 1, curveSegments: 8,
    bevelEnabled: true, bevelThickness: 0.04, bevelSize: 0.035, bevelSegments: 2,
  });
  geo.translate(0, 0, -0.06);
  return geo;
}

/** Elite affix tints, blended on top of the base gold elite tint (see enemyColor()). */
const AFFIX_TINTS: { bit: number; color: number }[] = [
  { bit: AFFIX_BITS.shielded, color: 0xaeeeff }, // pale cyan-white — only while shield[i] > 0
  { bit: AFFIX_BITS.hasted, color: 0xff5a1a }, // orange-red
  { bit: AFFIX_BITS.splitting, color: 0x5aff7a }, // green
  { bit: AFFIX_BITS.volatile, color: 0xff40ff }, // magenta
  { bit: AFFIX_BITS.leeching, color: 0x8a0000 }, // deep red
  { bit: AFFIX_BITS.warded, color: 0x9a4aff }, // purple
];
const _affixColor = new THREE.Color();
const _affixTmp = new THREE.Color();

function makeInstanced(geo: THREE.BufferGeometry, mat: THREE.Material, cap: number, shadows = false): THREE.InstancedMesh {
  const m = new THREE.InstancedMesh(geo, mat, cap);
  m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  m.count = 0;
  m.frustumCulled = false;
  m.castShadow = shadows;
  m.receiveShadow = false;
  // allocate instanceColor up-front so we can write it every frame
  m.setColorAt(0, WHITE);
  if (m.instanceColor) m.instanceColor.setUsage(THREE.DynamicDrawUsage);
  return m;
}

/**
 * InstancedViews: draws every pooled entity kind with one InstancedMesh per
 * shape. Each frame we walk the SoA arrays, interpolate, and write matrices.
 * Draw calls: ~6 bullet shapes + 1 outline + ~9 enemy shapes + pickups + hazards.
 */
export class InstancedViews {
  private playerBullets: THREE.InstancedMesh[] = [];
  private playerTrails: THREE.InstancedMesh[] = [];
  private enemyBullets: THREE.InstancedMesh[] = [];
  private enemyTrails: THREE.InstancedMesh[] = [];
  private enemyBulletHalo: THREE.InstancedMesh;
  /** Flat list of every enemy bucket (primitives first, then GLTF models).
   * Per-enemy and per-boss lookups are precomputed indices into this array —
   * the per-frame loop never does a Map/string lookup. */
  private buckets: EnemyBucket[] = [];
  private primBucketOf = new Map<PrimitiveGeo, number>();
  private modelBucketOf = new Map<string, number>();
  private bucketCounts: Int32Array = new Int32Array(0);
  /** Toggle outlines (quality 'low' turns them off). */
  outlines = true;
  projectileTrails = true;
  highContrastProjectiles = false;
  reducedFlash = false;
  colorblindMode: ColorblindMode = 'none';
  private cameraDistance = 30;
  private enemyHaloMaterial: THREE.MeshBasicMaterial;
  private pickupMesh: THREE.InstancedMesh;
  private heartMesh: THREE.InstancedMesh;
  private heartFacing = new THREE.Quaternion().setFromEuler(new THREE.Euler(-52 * Math.PI / 180, Math.PI / 4, 0, 'YXZ'));
  private hazardMesh: THREE.InstancedMesh;
  private petMeshes: THREE.InstancedMesh[] = [];
  private petCounts = new Int32Array(PETS.length);
  private visualOfEnemyDef: VisualDef[] = [];
  private bucketOfEnemyDef: Int32Array = new Int32Array(0);
  private bossVisual: VisualDef | null = null;
  private bossDef: BossDef | null = null;
  private bossBucket = -1;
  private chargerOfEnemyDef: Uint8Array;
  private time = 0;
  private eraIndex = 0;

  constructor(readonly scene: THREE.Scene, reg: ContentRegistry, models: ModelLibrary) {
    const trailGeo = new THREE.CylinderGeometry(0.2, 0.015, 1, 6);
    trailGeo.rotateX(Math.PI / 2);
    for (const look of PLAYER_PROJECTILE_LOOKS) {
      const pm = new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false });
      const trailMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: look.trailOpacity, depthWrite: false, toneMapped: false, blending: THREE.AdditiveBlending });
      const a = makeInstanced(look.geometry, pm, MAX_PROJECTILES);
      const trail = makeInstanced(trailGeo, trailMat, MAX_PROJECTILES);
      this.playerBullets.push(a);
      this.playerTrails.push(trail);
      scene.add(trail, a);
    }
    for (const look of ENEMY_PROJECTILE_LOOKS) {
      const em = new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false });
      const trailMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: look.trailOpacity, depthWrite: false, toneMapped: false, blending: THREE.AdditiveBlending });
      const b = makeInstanced(look.geometry, em, MAX_PROJECTILES);
      const trail = makeInstanced(trailGeo, trailMat, MAX_PROJECTILES);
      this.enemyBullets.push(b);
      this.enemyTrails.push(trail);
      scene.add(trail, b);
    }
    // dark halo under enemy bullets: guarantees contrast against any background
    this.enemyHaloMaterial = new THREE.MeshBasicMaterial({ color: 0x120018, transparent: true, opacity: 0.55, depthWrite: false });
    this.enemyBulletHalo = makeInstanced(
      new THREE.SphereGeometry(0.5, 8, 6),
      this.enemyHaloMaterial,
      MAX_PROJECTILES,
    );
    this.enemyBulletHalo.renderOrder = -1;
    scene.add(this.enemyBulletHalo);

    for (const g of GEOS) {
      const mat = toonMaterial(0xffffff, 0x000000, 0);
      const m = makeInstanced(primitiveGeometry(g), mat, MAX_ENEMIES, true);
      const o = instancedOutline(m, 0.06);
      this.primBucketOf.set(g, this.buckets.length);
      this.buckets.push({ mesh: m, outline: o, isModel: false });
      scene.add(m, o);
    }
    // one extra InstancedMesh (+ outline) per distinct loaded GLTF model
    // referenced by an enemy/boss — draw calls stay per-model, not per-instance.
    const modelPaths = new Set<string>();
    for (const def of reg.enemyList) if (def.visual.gltf && models.has(def.visual.gltf)) modelPaths.add(def.visual.gltf);
    for (const boss of reg.bosses.values()) if (boss.visual.gltf && models.has(boss.visual.gltf)) modelPaths.add(boss.visual.gltf);
    for (const path of modelPaths) {
      const geo = models.get(path)!;
      const sample = reg.enemyList.find((def) => def.visual.gltf === path)?.visual
        ?? [...reg.bosses.values()].find((def) => def.visual.gltf === path)!.visual;
      const mat = models.materialFor(sample);
      const m = makeInstanced(geo, mat, MAX_ENEMIES, true);
      const o = instancedOutline(m, 0.06);
      this.modelBucketOf.set(path, this.buckets.length);
      this.buckets.push({ mesh: m, outline: o, isModel: true });
      scene.add(m, o);
    }
    this.bucketCounts = new Int32Array(this.buckets.length);

    this.chargerOfEnemyDef = new Uint8Array(reg.enemyList.length);
    this.bucketOfEnemyDef = new Int32Array(reg.enemyList.length);
    reg.enemyList.forEach((def, i) => {
      this.visualOfEnemyDef.push(def.visual);
      const modelBucket = def.visual.gltf ? this.modelBucketOf.get(def.visual.gltf) : undefined;
      this.bucketOfEnemyDef[i] = modelBucket ?? this.primBucketOf.get(def.visual.geo)!;
      this.chargerOfEnemyDef[i] = def.behavior.type === 'charge' || def.behavior.type === 'ram' || def.behavior.type === 'flyover' || def.behavior.type === 'assemble' || def.behavior.type === 'lockon' || def.behavior.type === 'unwind' ? 1 : 0;
    });

    this.pickupMesh = makeInstanced(new THREE.OctahedronGeometry(0.28, 0), new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false }), MAX_PICKUPS);
    scene.add(this.pickupMesh);
    this.heartMesh = makeInstanced(heartGeometry(), toonMaterial(0xff254b, 0xff102e, 0.35), MAX_PICKUPS);
    scene.add(this.heartMesh);

    const hz = new THREE.CircleGeometry(1, 20);
    hz.rotateX(-Math.PI / 2);
    this.hazardMesh = makeInstanced(hz, new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.4, depthWrite: false }), MAX_HAZARDS);
    this.hazardMesh.position.y = 0.05;
    scene.add(this.hazardMesh);
    for (const pet of PETS) {
      const mesh = makeInstanced(primitiveGeometry(pet.geo), toonMaterial(pet.color, 0x000000, 0.15), MAX_PETS, true);
      this.petMeshes.push(mesh);
      scene.add(mesh);
    }
  }

  setBoss(def: BossDef | null): void {
    this.bossDef = def;
    this.bossVisual = def?.visual ?? null;
    if (!def) {
      this.bossBucket = -1;
      return;
    }
    const modelBucket = def.visual.gltf ? this.modelBucketOf.get(def.visual.gltf) : undefined;
    this.bossBucket = modelBucket ?? this.primBucketOf.get(def.visual.geo)!;
  }

  setEra(index: number): void { this.eraIndex = Math.max(0, Math.min(9, index | 0)); }

  /** Wind-up telegraph: true while a charger/ram/flyover/assemble/lockon/unwind is heaving, locked, or
   * frozen mid-snap, or a boss is in a charge phase or the wing-beat state of a `flight` special, the
   * pressure-windup state of `overheat`, the deploying state of `subsystems`, or the rooted windup of `devour`. */
  private isWindingUp(w: World, i: number): boolean {
    const e = w.enemies;
    if (e.state[i] !== 1) return false;
    if (e.isBoss[i]) {
      const ph = this.bossDef?.phases[e.phase[i]];
      return ph?.behavior.type === 'charge' || ph?.special?.type === 'flight' || ph?.special?.type === 'overheat' || ph?.special?.type === 'subsystems' || ph?.special?.type === 'devour';
    }
    return this.chargerOfEnemyDef[e.def[i]] === 1;
  }

  /** Enemy body colour (instance colour) — flashes white when hit, tinted by
   * status. GLTF-backed buckets bake their own colour into vertex colors, so
   * they start from white (multiplied with the material's vertexColors)
   * rather than `visual.color`. */
  private enemyColor(w: World, i: number, v: VisualDef, isModel: boolean, out: THREE.Color): THREE.Color {
    const e = w.enemies;
    if (e.hitFlash[i] > 0) return out.copy(WHITE);
    if (isModel) out.copy(WHITE);
    else out.set(v.color);
    if (this.isWindingUp(w, i)) return out.lerp(_c.set(0xff2a2a), 0.55 + 0.45 * Math.sin(this.time * 24));
    if (e.slow[i] > 0) out.lerp(_c.set(0x8fd8ff), 0.5);
    if (e.burnT[i] > 0) out.lerp(_c.set(0xff7a2a), 0.5);
    if (e.elite[i]) {
      out.lerp(_c.set(0xffe680), 0.25);
      const mask = e.affixes[i];
      if (mask) {
        _affixColor.setRGB(0, 0, 0);
        let n = 0;
        for (const t of AFFIX_TINTS) {
          if ((mask & t.bit) === 0) continue;
          if (t.bit === AFFIX_BITS.shielded && e.shield[i] <= 0) continue; // broken shield → normal gold
          _affixColor.add(_affixTmp.set(t.color));
          n++;
        }
        if (n > 0) {
          _affixColor.multiplyScalar(1 / n);
          out.lerp(_affixColor, 0.4);
        }
      }
    }
    if (e.invuln[i] > 0) out.lerp(_c.set(0xffffff), this.reducedFlash ? 0.35 : 0.5 + 0.5 * Math.sin(this.time * 20));
    if (this.colorblindMode !== 'none') out.lerp(_c.set(this.enemyPalette()), 0.42);
    return out;
  }

  private playerPalette(): number {
    return this.colorblindMode === 'protanopia' ? 0x00d8ff : this.colorblindMode === 'deuteranopia' ? 0x3979ff : this.colorblindMode === 'tritanopia' ? 0x00d084 : 0x62e5ff;
  }

  private enemyPalette(): number {
    return this.colorblindMode === 'protanopia' ? 0xffc400 : this.colorblindMode === 'deuteranopia' ? 0xff9418 : this.colorblindMode === 'tritanopia' ? 0xff4fa3 : 0xff3d71;
  }

  /** Camera-relative rendering: every world position is drawn at cam + wrapDelta(pos − cam). */
  private camX = 0;
  private camZ = 0;
  private half = 60;
  private rx(x: number): number {
    return this.camX + wrapDelta(x - this.camX, this.half);
  }
  private rz(z: number): number {
    return this.camZ + wrapDelta(z - this.camZ, this.half);
  }

  update(w: World, alpha: number, frameDt: number, camX = 0, camZ = 0, cameraQuaternion?: THREE.Quaternion, cameraDistance = 30): void {
    this.time += frameDt;
    this.camX = camX;
    this.camZ = camZ;
    this.half = w.half;
    this.cameraDistance = cameraDistance;
    this.enemyHaloMaterial.opacity = this.highContrastProjectiles ? 0.82 : 0.55;
    if (cameraQuaternion) this.heartFacing.copy(cameraQuaternion);
    this.updateProjectiles(w, alpha);
    this.updateEnemies(w, alpha);
    this.updatePets(w, alpha);
    this.updatePickups(w);
    this.updateHazards(w);
  }

  private updateProjectiles(w: World, alpha: number): void {
    const p = w.projectiles;
    const nP = this.playerBullets.length;
    const nE = this.enemyBullets.length;
    // per-visual counters (small fixed arrays → no allocation)
    const cntP = this._cntP;
    const cntE = this._cntE;
    cntP.fill(0);
    cntE.fill(0);
    let halo = 0;
    const high = p.pool.high;
    for (let i = 1; i < high; i++) {
      if (!p.pool.alive[i]) continue;
      const x = this.rx(p.px[i] + (p.x[i] - p.px[i]) * alpha);
      const z = this.rz(p.py[i] + (p.y[i] - p.py[i]) * alpha);
      const v = p.visual[i];
      const r = p.radius[i];
      // Keep projectiles legible when the player chooses a wider view. This is
      // presentation-only: collision radii remain untouched in the simulation.
      const readability = Math.max(1, Math.min(44, this.cameraDistance) / 30);
      const heading = Math.atan2(p.vy[i], p.vx[i]);
      _q.setFromAxisAngle(_up, -heading);
      _p.set(x, 0.9, z);
      if (p.side[i] === 0) {
        const si = projectileLookIndex(p.color[i], p.visual[i], p.elements[i], i);
        const look = PLAYER_PROJECTILE_LOOKS[si];
        const mesh = this.playerBullets[si];
        const trail = this.playerTrails[si];
        const k = cntP[si]++;
        const sc = r * 2.4 * readability;
        _q.setFromAxisAngle(_up, Math.PI / 2 - heading);
        if (look.spin > 0) _q.multiply(_qs.setFromAxisAngle(_fwd, this.time * look.spin + i));
        _s.set(sc * look.width, sc * look.width, sc * look.length);
        _m.compose(_p, _q, _s);
        mesh.setMatrixAt(k, _m);
        _c.set(p.elements[i] ? look.color : p.color[i]);
        if (this.highContrastProjectiles || this.colorblindMode !== 'none') _c.lerp(_elColor.set(this.playerPalette()), this.colorblindMode === 'none' ? 0.4 : 0.72);
        mesh.setColorAt(k, _c);
        const speed = Math.hypot(p.vx[i], p.vy[i]) || 1;
        const trailWorld = Math.min(1.8, look.trailLength * sc);
        _p.set(x - p.vx[i] / speed * trailWorld * 0.5, 0.9, z - p.vy[i] / speed * trailWorld * 0.5);
        _q.setFromAxisAngle(_up, Math.PI / 2 - heading);
        _s.set(sc * look.width * 0.8, sc * look.width * 0.8, trailWorld);
        _m.compose(_p, _q, _s);
        trail.setMatrixAt(k, _m);
        trail.setColorAt(k, _c);
      } else {
        const style = enemyProjectileLookIndex(p.color[i], v, this.eraIndex);
        const look = ENEMY_PROJECTILE_LOOKS[style];
        const mesh = this.enemyBullets[style];
        const trail = this.enemyTrails[style];
        const k = cntE[style]++;
        const sc = r * 2.6 * readability;
        _q.setFromAxisAngle(_up, Math.PI / 2 - heading);
        if (look.spin !== 0) _q.multiply(_qs.setFromAxisAngle(_fwd, this.time * look.spin + i));
        _s.set(sc * look.width, sc * look.width, sc * look.length);
        _m.compose(_p, _q, _s);
        mesh.setMatrixAt(k, _m);
        _c.set(p.color[i]);
        if (this.highContrastProjectiles || this.colorblindMode !== 'none') _c.lerp(_elColor.set(this.enemyPalette()), this.colorblindMode === 'none' ? 0.55 : 0.78);
        mesh.setColorAt(k, _c);
        const speed = Math.hypot(p.vx[i], p.vy[i]) || 1;
        const trailWorld = Math.min(2.4, look.trailLength * sc);
        _p.set(x - p.vx[i] / speed * trailWorld * .5, .9, z - p.vy[i] / speed * trailWorld * .5);
        _q.setFromAxisAngle(_up, Math.PI / 2 - heading);
        _s.set(sc * look.width * .72, sc * look.width * .72, trailWorld);
        _m.compose(_p, _q, _s);
        trail.setMatrixAt(k, _m);
        trail.setColorAt(k, _c);
        _s.setScalar(r * (this.highContrastProjectiles ? 4.4 : 3.6) * readability);
        _p.set(x, .9, z);
        _m.compose(_p, _q, _s);
        this.enemyBulletHalo.setMatrixAt(halo++, _m);
      }
    }
    for (let v = 0; v < nP; v++) {
      const a = this.playerBullets[v];
      a.count = cntP[v];
      a.instanceMatrix.needsUpdate = true;
      if (a.instanceColor) a.instanceColor.needsUpdate = true;
      const trail = this.playerTrails[v];
      trail.count = this.projectileTrails ? cntP[v] : 0;
      trail.visible = this.projectileTrails;
      if (this.projectileTrails) {
        trail.instanceMatrix.needsUpdate = true;
        if (trail.instanceColor) trail.instanceColor.needsUpdate = true;
      }
    }
    for (let v = 0; v < nE; v++) {
      const b = this.enemyBullets[v];
      b.count = cntE[v];
      b.instanceMatrix.needsUpdate = true;
      if (b.instanceColor) b.instanceColor.needsUpdate = true;
      const trail = this.enemyTrails[v];
      trail.count = this.projectileTrails ? cntE[v] : 0;
      trail.visible = this.projectileTrails;
      if (this.projectileTrails) {
        trail.instanceMatrix.needsUpdate = true;
        if (trail.instanceColor) trail.instanceColor.needsUpdate = true;
      }
    }
    this.enemyBulletHalo.count = halo;
    this.enemyBulletHalo.instanceMatrix.needsUpdate = true;
  }
  private _cntP = new Int32Array(PLAYER_PROJECTILE_LOOKS.length);
  private _cntE = new Int32Array(ENEMY_PROJECTILE_LOOKS.length);

  private updateEnemies(w: World, alpha: number): void {
    const e = w.enemies;
    const cnt = this.bucketCounts;
    cnt.fill(0);
    const high = e.pool.high;
    const t = this.time;
    for (let i = 1; i < high; i++) {
      if (!e.pool.alive[i]) continue;
      const isBoss = e.isBoss[i] === 1;
      const vis = isBoss ? this.bossVisual : this.visualOfEnemyDef[e.def[i]];
      if (!vis) continue;
      const bi = isBoss ? this.bossBucket : this.bucketOfEnemyDef[e.def[i]];
      if (bi < 0) continue;
      const bucket = this.buckets[bi];
      const mesh = bucket.mesh;
      const k = cnt[bi]++;
      const x = this.rx(e.px[i] + (e.x[i] - e.px[i]) * alpha);
      const z = this.rz(e.py[i] + (e.y[i] - e.py[i]) * alpha);
      const r = e.radius[i];
      let sy = 1;
      let sxz = 1;
      let bob = 0;
      switch (vis.anim) {
        case 'bob':
          bob = Math.sin(t * 3 + i) * 0.15;
          break;
        case 'pulse': {
          const s = 1 + Math.sin(t * 4 + i) * 0.06;
          sxz = s;
          sy = 2 - s;
          break;
        }
        case 'wobble':
          sxz = 1 + Math.sin(t * 6 + i) * 0.08;
          sy = 1 - Math.sin(t * 6 + i) * 0.08;
          break;
      }
      const scale = (vis.scale ?? 1) * 2 * r;
      // charge wind-up: crouch and stretch toward the facing direction, growing as the charge nears
      let sLong = 1;
      if (this.isWindingUp(w, i)) {
        const k = 0.5 + 0.5 * Math.sin(t * 18);
        sLong = 1.35 + 0.25 * k;
        sy *= 0.75;
        sxz *= 0.9;
      }
      _p.set(x, r * (vis.scaleY ?? 1) * 0.9 + bob + e.z[i], z);
      if (vis.anim === 'spin' && sLong === 1) _q.setFromAxisAngle(_up, t * 2 + i);
      else _q.setFromAxisAngle(_up, -e.facing[i] + Math.PI / 2);
      _s.set(scale * sxz, scale * sy * (vis.scaleY ?? 1), scale * sxz * sLong);
      _m.compose(_p, _q, _s);
      mesh.setMatrixAt(k, _m);
      mesh.setColorAt(k, this.enemyColor(w, i, vis, bucket.isModel, _c));
    }
    for (let bi = 0; bi < this.buckets.length; bi++) {
      const bucket = this.buckets[bi];
      bucket.mesh.count = cnt[bi];
      bucket.mesh.instanceMatrix.needsUpdate = true;
      bucket.outline.count = this.outlines ? cnt[bi] : 0;
      bucket.outline.visible = this.outlines;
      if (bucket.mesh.instanceColor) bucket.mesh.instanceColor.needsUpdate = true;
    }
  }

  private updatePickups(w: World): void {
    const pk = w.pickups;
    let n = 0;
    let hearts = 0;
    const t = this.time;
    for (let i = 1; i < pk.pool.high; i++) {
      if (!pk.pool.alive[i]) continue;
      const kind = pk.kind[i];
      if (kind === 1) {
        _p.set(this.rx(pk.x[i]), 0.7 + Math.sin(t * 3 + i) * 0.1, this.rz(pk.y[i]));
        // Face the camera without spinning edge-on; pulse is purely cosmetic.
        _s.setScalar(1.15 + Math.sin(t * 5 + i) * 0.07);
        _m.compose(_p, this.heartFacing, _s);
        this.heartMesh.setMatrixAt(hearts++, _m);
        continue;
      }
      _p.set(this.rx(pk.x[i]), 0.5 + Math.sin(t * 4 + i) * 0.1, this.rz(pk.y[i]));
      _q.setFromAxisAngle(_up, t * 2 + i);
      const s = kind === 0 ? Math.min(2.2, 0.8 + pk.amount[i] * 0.05) : 1.4;
      _s.setScalar(s);
      _m.compose(_p, _q, _s);
      this.pickupMesh.setMatrixAt(n, _m);
      this.pickupMesh.setColorAt(n, _c.set(kind === 0 ? 0x5dff9c : 0xffd84a));
      n++;
    }
    this.pickupMesh.count = n;
    this.pickupMesh.instanceMatrix.needsUpdate = true;
    if (this.pickupMesh.instanceColor) this.pickupMesh.instanceColor.needsUpdate = true;
    this.heartMesh.count = hearts;
    this.heartMesh.instanceMatrix.needsUpdate = true;
  }

  private updatePets(w: World, alpha: number): void {
    const counts = this.petCounts;
    counts.fill(0);
    const pets = w.pets;
    for (let i = 1; i < pets.pool.high; i++) {
      if (!pets.pool.alive[i]) continue;
      const defIndex = pets.def[i];
      const mesh = this.petMeshes[defIndex];
      if (!mesh) continue;
      const k = counts[defIndex]++;
      const x = this.rx(pets.px[i] + (pets.x[i] - pets.px[i]) * alpha);
      const z = this.rz(pets.py[i] + (pets.y[i] - pets.py[i]) * alpha);
      _p.set(x, 0.75 + Math.sin(this.time * 4 + i) * 0.1, z);
      _q.setFromAxisAngle(_up, -pets.facing[i] + Math.PI / 2);
      _s.setScalar(0.48);
      _m.compose(_p, _q, _s);
      mesh.setMatrixAt(k, _m);
    }
    this.petMeshes.forEach((mesh, i) => {
      mesh.count = counts[i];
      mesh.instanceMatrix.needsUpdate = true;
    });
  }

  private updateHazards(w: World): void {
    const h = w.hazards;
    let n = 0;
    const t = this.time;
    for (let i = 1; i < h.pool.high; i++) {
      if (!h.pool.alive[i]) continue;
      const f = h.life[i] / h.maxLife[i];
      _p.set(this.rx(h.x[i]), 0, this.rz(h.y[i]));
      _q.identity();
      const arming = h.armT[i] > 0;
      // telegraph (armT > 0): a fast-blinking disc that swells to full size as it arms — harmless until it lands
      const s = arming
        ? h.radius[i] * (0.35 + 0.65 * (1 - Math.min(1, h.armT[i] / 1.2))) * (0.85 + 0.15 * Math.sin(t * 22 + i))
        : h.radius[i] * (0.9 + 0.1 * Math.sin(t * 6 + i)) * (f < 0.2 ? f / 0.2 : 1);
      _s.set(s, 1, s);
      _m.compose(_p, _q, _s);
      this.hazardMesh.setMatrixAt(n, _m);
      _c.set(h.color[i]);
      if (arming) _c.multiplyScalar(0.55 + 0.45 * Math.sin(t * 22 + i));
      this.hazardMesh.setColorAt(n, _c);
      n++;
    }
    this.hazardMesh.count = n;
    this.hazardMesh.instanceMatrix.needsUpdate = true;
    if (this.hazardMesh.instanceColor) this.hazardMesh.instanceColor.needsUpdate = true;
  }
}
